/**
 * Temporary sign-in passwords for Team members (Customer > Configure > Team).
 * These are pure functions with no dependencies, so `npm run test:unit` exercises them with
 * Node's runner.
 *
 * WHY THIS EXISTS: email invites must be sent from the GuestTek email domain, and that sender
 * is not available yet (2026-10-07). Until it is, an admin creates the login with a temporary
 * password, passes it on out-of-band (in person or by phone), and the sign-in service forces the
 * person to choose their own at first sign-in, then set up an authenticator app.
 *
 * The password is generated HERE, in the browser, with crypto.getRandomValues. The server
 * forwards it to the identity provider and never echoes, logs or stores it. That makes this
 * screen the only place it is ever shown, exactly once.
 *
 * The rules MIRROR the realm policy reported by GET /operators/preflight (`password_policy`).
 * The identity provider is the authority and re-checks every write. Mirroring the rules lets
 * the form explain itself live instead of bouncing a 400.
 */

/** Realm policy as reported by preflight. Every field is optional because older servers omit it. */
export interface PasswordPolicy {
  min_length?: number;
  max_length?: number | null;
  digits?: number;
  upper_case?: number;
  lower_case?: number;
  special_chars?: number;
  not_username?: boolean;
  not_email?: boolean;
  history?: number;
}

/** The app floor, mirroring the realm's length(14). It is used when preflight has not loaded. */
export const MIN_LENGTH_FLOOR = 14;
/** Generated length: comfortably above the floor and still easy to read out in 4-char groups. */
export const GENERATED_LENGTH = 20;

/*
 * Alphabets with LOOK-ALIKES REMOVED (0/O/o, 1/l/I, 5/S, 2/Z, 8/B). The password is read aloud
 * or copied by hand from a screen, and one misread character is a failed first sign-in. The
 * symbols are ones that are easy to say and safe in every keyboard layout.
 */
export const UPPER = "ACDEFGHJKLMNPQRTUVWXY";
export const LOWER = "abcdefghijkmnpqrstuvwxyz";
export const DIGITS = "34679";
export const SYMBOLS = "-+=@#%";
const ALL = UPPER + LOWER + DIGITS + SYMBOLS;

/** Uniform random index in [0, n) from a byte source, rejecting biased bytes (no modulo bias). */
function randomIndex(n: number, nextByte: () => number): number {
  const limit = 256 - (256 % n);
  for (;;) {
    const b = nextByte();
    if (b < limit) return b % n;
  }
}

/** Default byte source: the Web Crypto CSPRNG (browser and Node 22 both expose it). */
function cryptoByte(): number {
  const buf = new Uint8Array(1);
  globalThis.crypto.getRandomValues(buf);
  return buf[0] ?? 0; // always defined for a 1-byte buffer; satisfies noUncheckedIndexedAccess
}

/**
 * A strong temporary password that satisfies `policy`. The length is the larger of
 * GENERATED_LENGTH and the policy minimum, capped by max_length. The result has at least one
 * character of each class, or more where the policy asks for more. It is shuffled so the
 * classes do not sit in fixed positions. `nextByte` is injectable so the unit test can be
 * deterministic.
 */
export function generateTempPassword(
  policy: PasswordPolicy = {},
  nextByte: () => number = cryptoByte,
): string {
  const min = Math.max(policy.min_length ?? MIN_LENGTH_FLOOR, MIN_LENGTH_FLOOR);
  let length = Math.max(GENERATED_LENGTH, min);
  if (policy.max_length && policy.max_length >= min) length = Math.min(length, policy.max_length);

  const pick = (set: string) => set[randomIndex(set.length, nextByte)] ?? "";
  const chars: string[] = [];
  const need: Array<[string, number]> = [
    [UPPER, Math.max(1, policy.upper_case ?? 0)],
    [LOWER, Math.max(1, policy.lower_case ?? 0)],
    [DIGITS, Math.max(1, policy.digits ?? 0)],
    [SYMBOLS, Math.max(1, policy.special_chars ?? 0)],
  ];
  for (const [set, count] of need) for (let i = 0; i < count; i++) chars.push(pick(set));
  while (chars.length < length) chars.push(pick(ALL));

  // Fisher-Yates with the same unbiased source.
  for (let i = chars.length - 1; i > 0; i--) {
    const j = randomIndex(i + 1, nextByte);
    [chars[i], chars[j]] = [chars[j] ?? "", chars[i] ?? ""];
  }
  return chars.join("");
}

export interface PasswordCheck {
  id: "length" | "not_username" | "not_email" | "classes";
  label: string;
  ok: boolean;
}

/** Count characters matching a predicate. */
function count(pw: string, test: (ch: string) => boolean): number {
  let n = 0;
  for (const ch of pw) if (test(ch)) n++;
  return n;
}

/**
 * The live checklist for a typed temporary password. It stays deliberately short: only rules
 * the person can act on. Password history cannot be checked here (the server knows, the
 * browser does not), so a refusal for reuse arrives as the server's own message.
 */
export function checkTempPassword(
  pw: string,
  ctx: { username: string; email?: string | null; policy?: PasswordPolicy },
): { ok: boolean; checks: PasswordCheck[] } {
  const policy = ctx.policy ?? {};
  const min = Math.max(policy.min_length ?? MIN_LENGTH_FLOOR, MIN_LENGTH_FLOOR);
  const lower = pw.toLowerCase();
  const user = ctx.username.trim().toLowerCase();
  const email = (ctx.email ?? "").trim().toLowerCase();
  const checks: PasswordCheck[] = [
    { id: "length", label: `At least ${min} characters`, ok: pw.length >= min },
  ];
  if (policy.max_length) {
    checks[0] = {
      id: "length",
      label: `${min}–${policy.max_length} characters`,
      ok: pw.length >= min && pw.length <= policy.max_length,
    };
  }
  // The realm checks "not equal". Here it is "does not contain", which is stricter, so the
  // form never promises something the server will refuse.
  if (policy.not_username !== false) {
    checks.push({
      id: "not_username",
      label: "Does not contain their username",
      ok: pw.length > 0 && (!user || !lower.includes(user)),
    });
  }
  if (policy.not_email && email) {
    checks.push({
      id: "not_email",
      label: "Does not contain their email address",
      ok: pw.length > 0 && !lower.includes(email),
    });
  }
  const wants = {
    upper: policy.upper_case ?? 0,
    lower: policy.lower_case ?? 0,
    digits: policy.digits ?? 0,
    special: policy.special_chars ?? 0,
  };
  if (wants.upper || wants.lower || wants.digits || wants.special) {
    const ok =
      count(pw, (c) => c >= "A" && c <= "Z") >= wants.upper &&
      count(pw, (c) => c >= "a" && c <= "z") >= wants.lower &&
      count(pw, (c) => c >= "0" && c <= "9") >= wants.digits &&
      count(pw, (c) => !/[A-Za-z0-9]/.test(c)) >= wants.special;
    checks.push({ id: "classes", label: "Uses the required mix of characters", ok });
  }
  return { ok: checks.every((c) => c.ok), checks };
}

/**
 * Split a password into groups of 4 for the handover display ("Tq7# vR2m Lp9x ..."). The
 * groups are DISPLAY ONLY. Copy always copies the raw value, and the gaps are rendered with
 * CSS spacing rather than characters, so a manual select-and-copy cannot pick up spaces either.
 */
export function chunkForReading(pw: string, size = 4): string[] {
  const out: string[] = [];
  for (let i = 0; i < pw.length; i += size) out.push(pw.slice(i, i + size));
  return out;
}

/** The sign-in method for a new Team member. */
export type SignInMethod = "temp_password" | "invite";

/** The exact sentence used when the server sends no reason. */
export const INVITE_PENDING_FALLBACK =
  "Email invites will be sent from GuestTek email - not configured yet";

/**
 * Whether the invite option is selectable, and why not. This is driven ONLY by the server's
 * `invite_email_available`, so it lights up when the GuestTek sender is configured, with no
 * UI change. A missing flag (an older server) counts as unavailable. Failing closed means
 * nobody is invited from the wrong domain.
 */
export function inviteOption(preflight?: {
  invite_email_available?: boolean;
  invite_email_reason?: string | null;
} | null): { available: boolean; reason: string | null } {
  const available = preflight?.invite_email_available === true;
  return {
    available,
    reason: available ? null : preflight?.invite_email_reason || INVITE_PENDING_FALLBACK,
  };
}

/**
 * Can the Add-team-member form be submitted? This mirrors the server's create rules. The
 * profile (email, first and last name, as the realm requires: see lib/teamMember) must be
 * complete and valid on BOTH paths. A typed password must pass the checklist. An invite
 * needs the option to be available and an email. A preflight `ready: false` blocks creation
 * entirely.
 */
export function canSubmitCreate(s: {
  ready: boolean;
  username: string;
  method: SignInMethod;
  inviteAvailable: boolean;
  email: string;
  passwordOk: boolean;
  /** No entries from teamMember.profileErrors(). */
  profileOk: boolean;
}): boolean {
  if (!s.ready || !s.username.trim() || !s.profileOk) return false;
  if (s.method === "invite") return s.inviteAvailable && !!s.email.trim();
  return s.passwordOk;
}
